import type { TreatmentType, Phase, Track, GeneratedPlaylist, PlaylistHistory, TrackUsageEntry, PhaseOverrides } from '../types';
import { PHASE_RATIOS, PHASES_IN_ORDER, PHASE_OVERFLOW_SECONDS } from './constants';
import { calculateTrackWeight, getPhaseHistory } from './playlist-history';

// Lazy cache for playlist data (loaded on first use, not bundled in initial chunk)
let _tracksByPhase: Record<string, Track[]> | null = null;
let _trackEmbeddings: Record<string, number[]> | null = null;

async function getPlaylistData() {
  if (!_tracksByPhase || !_trackEmbeddings) {
    const [tracks, embeddings] = await Promise.all([
      import('../data/tracks'),
      import('../data/track-embeddings'),
    ]);
    _tracksByPhase = tracks.tracksByPhase;
    _trackEmbeddings = embeddings.trackEmbeddings;
  }
  return { tracksByPhase: _tracksByPhase, trackEmbeddings: _trackEmbeddings };
}

/**
 * Select an item using weighted random selection
 * Returns the index of the selected item
 */
export function weightedRandomSelect(weights: number[]): number {
  const totalWeight = weights.reduce((sum, w) => sum + (w > 0 ? w : 0), 0);
  if (totalWeight === 0) {
    // Fallback to uniform random if all weights are 0
    return Math.floor(Math.random() * weights.length);
  }

  let random = Math.random() * totalWeight;
  for (let i = 0; i < weights.length; i++) {
    const weight = weights[i];
    if (weight <= 0) continue;
    if (random < weight) return i;
    random -= weight;
  }
  // Fallback to last item (shouldn't happen)
  return weights.length - 1;
}

/**
 * Get Camelot wheel compatibility score between two keys
 * Same key: 1.0, Adjacent (±1): 0.9, Relative major/minor: 0.8, ±2: 0.6, Otherwise: 0.3
 */
export function getCamelotCompatibility(camelot1?: string, camelot2?: string): number {
  // Validate input - Camelot codes should be 2-3 chars (e.g., "8A", "12B")
  if (!camelot1 || !camelot2 || camelot1.length < 2 || camelot2.length < 2) {
    return 0.5; // Default for unknown or malformed
  }

  const num1 = parseInt(camelot1.slice(0, -1));
  const letter1 = camelot1.slice(-1);
  const num2 = parseInt(camelot2.slice(0, -1));
  const letter2 = camelot2.slice(-1);

  // Validate parsed values
  if (isNaN(num1) || isNaN(num2) || num1 < 1 || num1 > 12 || num2 < 1 || num2 > 12) {
    return 0.5;
  }
  if (letter1 !== 'A' && letter1 !== 'B') return 0.5;
  if (letter2 !== 'A' && letter2 !== 'B') return 0.5;

  // Same key
  if (camelot1 === camelot2) return 1.0;

  // Same number (relative major/minor)
  if (num1 === num2 && letter1 !== letter2) return 0.8;

  // Adjacent keys (same letter, ±1 on wheel, wrapping 12->1)
  if (letter1 === letter2) {
    const diff = Math.abs(num1 - num2);
    if (diff === 1 || diff === 11) return 0.9;
    if (diff === 2 || diff === 10) return 0.6;
  }

  return 0.3;
}

/**
 * Cosine similarity between two embedding vectors
 */
export function cosineSimilarity(v1?: number[], v2?: number[]): number {
  if (!v1 || !v2 || v1.length !== v2.length) return 0.5;

  let dot = 0;
  let mag1 = 0;
  let mag2 = 0;

  for (let i = 0; i < v1.length; i++) {
    dot += v1[i] * v2[i];
    mag1 += v1[i] * v1[i];
    mag2 += v2[i] * v2[i];
  }

  mag1 = Math.sqrt(mag1);
  mag2 = Math.sqrt(mag2);

  if (mag1 === 0 || mag2 === 0) return 0.5;
  return dot / (mag1 * mag2);
}

/**
 * Get embedding for a track by ISRC
 */
function getEmbedding(isrc: string, embeddings: Record<string, number[]>): number[] | undefined {
  return embeddings[isrc.toLowerCase()];
}

/**
 * Target energy envelopes per phase: [startEnergy, endEnergy]
 * Defines the desired energy arc through a phase.
 */
const ENERGY_ENVELOPES: Record<Phase, [number, number]> = {
  opening:  [0.3, 0.5],
  building: [0.5, 0.7],
  peak:     [0.7, 0.9],
  longTail: [0.8, 0.5],
  closing:  [0.5, 0.3],
};

/**
 * Get the target energy for a position within a phase via linear interpolation
 */
function getTargetEnergy(phase: Phase, progressRatio: number): number {
  const [start, end] = ENERGY_ENVELOPES[phase];
  return start + (end - start) * Math.min(1, Math.max(0, progressRatio));
}

/**
 * Score how well a track's energy fits the target energy curve at the current position.
 * Returns 0-1 (1 = perfect match).
 */
function scoreEnergyCurveFit(track: Track, phase: Phase, progressRatio: number): number {
  if (track.energy === undefined) return 0.5; // neutral if missing
  const target = getTargetEnergy(phase, progressRatio);
  const delta = Math.abs(track.energy - target);
  return Math.max(0, 1 - delta / 0.5);
}

/**
 * Jaccard genre similarity between two tracks.
 * Returns 0-1 (1 = identical genre sets, 0 = no overlap).
 * Returns 0.5 (neutral) if either track is missing genre data.
 */
function genreSimilarity(g1?: string[], g2?: string[]): number {
  if (!g1?.length || !g2?.length) return 0.5;
  const set1 = new Set(g1);
  const set2 = new Set(g2);
  const shared = [...set1].filter(g => set2.has(g)).length;
  return shared / new Set([...set1, ...set2]).size;
}

/**
 * Score a track transition for smart playlist ordering
 * Higher scores = better transitions
 *
 * Weights:
 *   Camelot harmony:     25%
 *   Embedding similarity: 20%
 *   Energy continuity:   15%
 *   Genre coherence:     15%
 *   Energy curve fit:    10%
 */
export function scoreTrackTransition(
  prevTrack: Track | null,
  nextTrack: Track,
  phase?: Phase,
  progressRatio?: number,
  embeddings?: Record<string, number[]>,
): number {
  // Base score
  let score = 1.0;

  // Energy curve fit (independent of previous track) - weight: 0.10
  if (phase !== undefined && progressRatio !== undefined) {
    const curveFit = scoreEnergyCurveFit(nextTrack, phase, progressRatio);
    score += curveFit * 0.10;
  }

  if (!prevTrack) return score;

  // Harmonic compatibility (Camelot wheel) - weight: 0.25
  if (prevTrack.camelot && nextTrack.camelot) {
    const camelotScore = getCamelotCompatibility(prevTrack.camelot, nextTrack.camelot);
    score += camelotScore * 0.25;
  }

  // Tempo compatibility (penalize >20 BPM jumps) - weight: 0.15
  // Disabled: BPM data is unreliable
  // if (prevTrack.bpm && nextTrack.bpm) {
  //   const bpmDelta = Math.abs(prevTrack.bpm - nextTrack.bpm);
  //   const bpmScore = Math.max(0, 1 - bpmDelta / 40);
  //   score += bpmScore * 0.15;
  // }

  // Embedding similarity - weight: 0.20
  if (embeddings) {
    const prevEmb = getEmbedding(prevTrack.isrc, embeddings);
    const nextEmb = getEmbedding(nextTrack.isrc, embeddings);
    if (prevEmb && nextEmb) {
      const embScore = (cosineSimilarity(prevEmb, nextEmb) + 1) / 2; // Normalize to 0-1
      score += embScore * 0.20;
    }
  }

  // Energy continuity (penalize >0.3 jumps) - weight: 0.15
  if (prevTrack.energy !== undefined && nextTrack.energy !== undefined) {
    const energyDelta = Math.abs(prevTrack.energy - nextTrack.energy);
    const energyScore = Math.max(0, 1 - energyDelta / 0.5);
    score += energyScore * 0.15;
  }

  // Genre coherence (Jaccard similarity) - weight: 0.15
  const genreScore = genreSimilarity(prevTrack.genres, nextTrack.genres);
  score += genreScore * 0.15;

  return score;
}

export const __test__ = {
  genreSimilarity,
  scoreEnergyCurveFit,
  scoreTrackTransition,
  getTargetEnergy,
};

/**
 * Calculate phase durations based on treatment type and total duration
 * If phaseOverrides are provided (as percentages), use them instead of default ratios
 */
function calculatePhaseDurations(
  treatmentType: TreatmentType,
  totalDurationMinutes: number,
  phaseOverrides?: PhaseOverrides
): Record<Phase, number> {
  const totalSeconds = totalDurationMinutes * 60;

  // If phase overrides are provided, convert percentages to seconds
  if (phaseOverrides) {
    const defaultRatios = PHASE_RATIOS[treatmentType];
    return {
      opening: Math.round(totalSeconds * (phaseOverrides.opening ?? defaultRatios.opening * 100) / 100),
      building: Math.round(totalSeconds * (phaseOverrides.building ?? defaultRatios.building * 100) / 100),
      peak: Math.round(totalSeconds * (phaseOverrides.peak ?? defaultRatios.peak * 100) / 100),
      longTail: Math.round(totalSeconds * (phaseOverrides.longTail ?? defaultRatios.longTail * 100) / 100),
      closing: Math.round(totalSeconds * (phaseOverrides.closing ?? defaultRatios.closing * 100) / 100),
    };
  }

  // Use default ratios for treatment type
  const ratios = PHASE_RATIOS[treatmentType];
  return {
    opening: Math.round(totalSeconds * ratios.opening),
    building: Math.round(totalSeconds * ratios.building),
    peak: Math.round(totalSeconds * ratios.peak),
    longTail: Math.round(totalSeconds * ratios.longTail),
    closing: Math.round(totalSeconds * ratios.closing),
  };
}

/**
 * Select tracks for a phase using smart ordering based on audio features
 * Combines audio-based transition scoring with history-based anti-repetition weighting
 * and energy curve enforcement
 */
function selectTracksForPhase(
  phase: Phase,
  phaseTracks: Track[],
  targetDuration: number,
  usedIsrcs: Set<string>,
  phaseHistory: TrackUsageEntry[],
  embeddings: Record<string, number[]>,
  lastTrackFromPreviousPhase?: Track
): { selected: Track[]; actualDuration: number } {
  const selected: Track[] = [];
  let actualDuration = 0;

  if (targetDuration <= 0) {
    return { selected, actualDuration };
  }

  // Create a pool of available tracks (not yet used in this playlist)
  const availablePool = phaseTracks.filter(t => !usedIsrcs.has(t.isrc));

  if (availablePool.length === 0) {
    return { selected, actualDuration };
  }

  // Keep selecting until we exceed target + overflow or run out of viable tracks
  while (availablePool.length > 0) {
    const prevTrack = selected.length > 0
      ? selected[selected.length - 1]
      : lastTrackFromPreviousPhase ?? null;

    // Progress through phase (0.0 = start, 1.0 = end) for energy curve targeting
    const progressRatio = targetDuration > 0 ? actualDuration / targetDuration : 0;

    // Calculate combined weights: history weight * audio transition score
    const weights = availablePool.map(track => {
      const historyWeight = calculateTrackWeight(track.isrc, phaseHistory);
      const transitionScore = scoreTrackTransition(prevTrack, track, phase, progressRatio, embeddings);
      return historyWeight * transitionScore;
    });

    // Select a track using weighted random
    const selectedIndex = weightedRandomSelect(weights);
    const track = availablePool[selectedIndex];

    // Check if adding this track would exceed target + overflow.
    // This prevents selecting e.g. an 11-minute track for a 3-minute phase.
    const wouldExceedOverflow =
      actualDuration + track.durationSeconds > targetDuration + PHASE_OVERFLOW_SECONDS;

    if (wouldExceedOverflow) {
      // Remove this track from consideration and try another
      availablePool.splice(selectedIndex, 1);
      continue;
    }

    // Add the selected track
    selected.push(track);
    actualDuration += track.durationSeconds;
    usedIsrcs.add(track.isrc);

    // Remove from available pool
    availablePool.splice(selectedIndex, 1);

    // Stop if we've reached the target duration
    if (actualDuration >= targetDuration) {
      break;
    }
  }

  // Fallback: if no tracks fit within the duration limit, select the shortest
  // available track to minimize overshoot (e.g., very short phase where all tracks
  // are longer than the target)
  if (selected.length === 0) {
    const remaining = phaseTracks.filter(t => !usedIsrcs.has(t.isrc));
    if (remaining.length > 0) {
      const shortest = remaining.reduce((min, t) =>
        t.durationSeconds < min.durationSeconds ? t : min
      );
      selected.push(shortest);
      actualDuration = shortest.durationSeconds;
      usedIsrcs.add(shortest.isrc);
    }
  }

  return { selected, actualDuration };
}

/**
 * Format date for playlist name (e.g., "January 22")
 */
function formatDate(): string {
  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'long' });
  const day = now.getDate();
  return `${month} ${day}`;
}

/**
 * Get treatment type label for playlist name
 */
function getTreatmentLabel(treatmentType: TreatmentType): string {
  const labels: Record<TreatmentType, string> = {
    iv: 'IV',
    im: 'IM',
    oral: 'Oral',
    spravato: 'Spravato',
  };
  return labels[treatmentType];
}

/**
 * Generate a playlist based on treatment type and duration
 * Optionally accepts history for anti-repetition weighting
 * Optionally accepts phaseOverrides as percentages (should sum to 100)
 */
export async function generatePlaylist(
  treatmentType: TreatmentType,
  durationMinutes: number,
  history?: PlaylistHistory,
  phaseOverrides?: PhaseOverrides
): Promise<GeneratedPlaylist> {
  const { tracksByPhase, trackEmbeddings } = await getPlaylistData();
  const phaseDurations = calculatePhaseDurations(treatmentType, durationMinutes, phaseOverrides);
  const usedIsrcs = new Set<string>();

  const phaseBreakdown: GeneratedPlaylist['phaseBreakdown'] = [];
  const allTracks: Track[] = [];
  let lastTrack: Track | undefined;

  // Select tracks for each phase in order, using smart ordering
  for (const phase of PHASES_IN_ORDER) {
    const phaseTracks = tracksByPhase[phase];
    const targetDuration = phaseDurations[phase];
    const phaseHistory = history ? getPhaseHistory(history, phase) : [];

    const { selected, actualDuration } = selectTracksForPhase(
      phase,
      phaseTracks,
      targetDuration,
      usedIsrcs,
      phaseHistory,
      trackEmbeddings,
      lastTrack
    );

    phaseBreakdown.push({
      phase,
      tracks: selected,
      targetDuration,
      actualDuration,
    });

    allTracks.push(...selected);

    // Track last selected track for smooth phase transitions
    if (selected.length > 0) {
      lastTrack = selected[selected.length - 1];
    }
  }

  const totalDuration = allTracks.reduce((sum, track) => sum + track.durationSeconds, 0);
  const treatmentLabel = getTreatmentLabel(treatmentType);
  const dateStr = formatDate();

  return {
    name: `${treatmentLabel} Journey ${durationMinutes}min ${dateStr}`,
    description: 'Generated by heal.fm',
    tracks: allTracks,
    totalDurationSeconds: totalDuration,
    phaseBreakdown,
  };
}

/**
 * Get ISRCs from a generated playlist
 */
export function getPlaylistIsrcs(playlist: GeneratedPlaylist): string[] {
  return playlist.tracks.map(track => track.isrc);
}

/**
 * Format duration in seconds to human-readable string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${secs}s`;
}

/**
 * Format minutes (including decimal) to human-readable string
 * e.g., 20.25 -> "20 min 15 sec", 60.5 -> "1 hr 0 min"
 */
export function formatMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.round((totalMinutes % 1) * 60);

  if (hours > 0) {
    return `${hours} hr ${minutes} min`;
  }
  if (seconds > 0) {
    return `${minutes} min ${seconds} sec`;
  }
  return `${minutes} min`;
}
